import { Award, Heart, Users } from 'lucide-react';
import Card from '../components/ui/Card';
import { useLanguage } from '../context/LanguageContext';
import { team } from '../data/team';

export default function About() {
  const { t, language } = useLanguage();

  const values = [
    {
      icon: Award,
      title: t('aboutPage.values.quality'),
      description: t('aboutPage.values.qualityDesc'),
      color: 'text-primary-600 dark:text-primary-400'
    },
    {
      icon: Heart,
      title: t('aboutPage.values.tradition'),
      description: t('aboutPage.values.traditionDesc'),
      color: 'text-wine-600 dark:text-wine-400'
    },
    {
      icon: Users,
      title: t('aboutPage.values.hospitality'),
      description: t('aboutPage.values.hospitalityDesc'),
      color: 'text-accent-600 dark:text-accent-400'
    }
  ];

  return (
    <div className="min-h-screen bg-cream-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-primary-800 dark:bg-primary-900 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('aboutPage.title')}
          </h1>
          <p className="text-lg text-primary-200 max-w-2xl mx-auto">
            {t('aboutPage.subtitle')}
          </p>
        </div>
      </div>

      {/* History Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=600&h=400&fit=crop"
                alt="Restaurant interior"
                className="rounded-2xl shadow-xl w-full"
              />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-accent-200 dark:bg-accent-900/30 rounded-full -z-10" />
            </div>

            {/* Content */}
            <div>
              <h2 className="font-display text-3xl font-bold text-gray-900 dark:text-white mb-6">
                {t('aboutPage.historyTitle')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                {t('aboutPage.historyText1')}
              </p>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('aboutPage.historyText2')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">
            {t('aboutPage.valuesTitle')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="text-center">
                  <div className="inline-flex p-4 rounded-2xl bg-cream-100 dark:bg-gray-700 mb-4">
                    <Icon className={`w-8 h-8 ${value.color}`} />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {value.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {t('aboutPage.teamTitle')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {t('aboutPage.teamSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member) => (
              <Card key={member.id} padding="none" hover className="overflow-hidden">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-display text-xl font-semibold text-gray-900 dark:text-white mb-1">
                    {member.name}
                  </h3>
                  <p className="text-accent-600 dark:text-accent-400 font-medium text-sm mb-3">
                    {member.role[language]}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {member.bio[language]}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
